import { type FC } from 'react';
import { useEffect, useMemo } from 'react';

import { ForbiddenErrors } from '../../api/errors/types';
import { PlayerConfigFetcher } from '../../api/fetchers/playerConfig/fetcher';
import { RESTRICTED_PAGE_URL } from '../../config/CoreConstants';
import { useGameConfig } from '../../config/GameConfigProvider';
import { UserSession } from '../../config/UserSession';
import { CrossOriginCommunication } from '../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { OutgoingMessage } from '../../controllers/crossOriginCommunication/types';
import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { useFeatureServiceConnected } from '../../services/features/reactBindings/hooks';
import { useQuickGuideService } from '../../services/quickGuide/reactBindings/hooks';
import { useUITranslationServiceConnected } from '../../services/uiTranslations/reactBindings/hooks';
import { CoreSessionStorage } from '../../storage/session/CoreSessionStorage';
import { useCasinoConfigFetcher } from '../../store/apiHooks/useCasinoConfigFetcher';
import { useEnvFetcher } from '../../store/apiHooks/useEnvFetcher';
import { usePlayerConfigFetcher } from '../../store/apiHooks/usePlayerConfigFetcher';
import { useStatsConfigFetcher } from '../../store/apiHooks/useStatsConfigFetcher';
import { useStatsSettingsFetcher } from '../../store/apiHooks/useStatsSettingsFetcher';
import { useTableConfigFetcher } from '../../store/apiHooks/useTableConfigFetcher';
import { useUserInfoFetcher } from '../../store/apiHooks/useUserInfoFetcher';
import { useVersionsFetcher } from '../../store/apiHooks/useVersionsFetcher';
import { useCoreDispatch } from '../../store/CoreStore';
import { useBetSlice } from '../../store/slices/bets/selectors';
import { betActions } from '../../store/slices/bets/slice';
import { chatActions } from '../../store/slices/chat/slice';
import { useChipSlice } from '../../store/slices/chips/selectors';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { configActions } from '../../store/slices/config/slice';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useInitializationSlice } from '../../store/slices/initialization/selectors';
import { initializationActions } from '../../store/slices/initialization/slice';
import { popupActions } from '../../store/slices/popup/slice';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { generateWelcomeMessage, LIVE_SUPPORT_WELCOME_MESSAGE } from '../../utils/chat';
import { getLanguage } from '../../utils/language';
import { Curtain } from '../curtain/Curtain';
import { useDragAndDropInit } from '../dragAndDrop/useDragAndDropInit';
import { useAutoplayBetDispatcher } from '../game/autoPlay/useAutoplayBetDispatcher';
import { PopupType } from '../popup/types';

import { useCurtain } from './useCurtain';
import { useFullscreen } from './useFullscreen';

export const GameInitializer: FC = (): null => {
  DeviceInfo.init();
  UserSession.clearUrlSession();

  const casinoConfig = useConfigSlice.casinoConfig();
  const chipsReady = useChipSlice.getAreChipsReady();
  const sessionLanguage = useConfigSlice.session()?.lang;
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const userInfoReady = useGameStateSlice.userInfoReady();
  const playerConfig = useConfigSlice.playerConfig();
  const currency = useGameStateSlice.getCurrency() ?? 'EUR';
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const hasTotalBet = useBetSlice.hasTotalBet();
  const errorCode = useInitializationSlice.getError()?.message;
  const currentLanguage = useSettingsSlice.currentLanguage();

  const dispatch = useCoreDispatch();

  const {
    config: {
      betConfig,
      defaultTimings,
      playAreaSize,
      tableConfigOptions,
      uiVolumeChannel,
      volumeChannels,
      translationConfig,
      isCrashGame,
      featureConfigs,
    },
  } = useGameConfig();

  /* Translations */
  const { isReady: areUITranslationsReady } = useUITranslationServiceConnected();

  /* Feature Flags */
  const { isReady: areFeatureConfigsReady } = useFeatureServiceConnected(featureConfigs);

  /* Quick Guide */
  useQuickGuideService(tableId);

  /* Language */
  useEffect(() => {
    const currentLanguage = getLanguage(translationConfig, sessionLanguage);
    dispatch(settingsActions.setLanguage(currentLanguage));
  }, [dispatch, sessionLanguage, translationConfig]);

  /* Session storage */
  useEffect(() => {
    const savedBets = tableId ? CoreSessionStorage.getLastBets(tableId) : null;
    if (savedBets) {
      dispatch(betActions.initLastBets({ bets: savedBets }));
    }
  }, [tableId, dispatch]);

  /* Timings */
  useEffect(() => {
    dispatch(configActions.setDefaultTimings(defaultTimings));
  }, [defaultTimings, dispatch]);

  /* Handle game close, exit, leave, quit */
  useEffect(() => {
    const handleGameCloseExitLeaveQuit = (): void => {
      if (casinoConfig?.notifyCloseContainer) {
        CrossOriginCommunication.dispatch(OutgoingMessage.CloseContainer, {
          tableId: tableConfig?.operatorGameId,
        });
      }
      if (casinoConfig?.returnMainPage) {
        CrossOriginCommunication.dispatch(OutgoingMessage.GameQuit, {
          tableId: tableConfig?.operatorGameId,
        });
      }
    };
    window.addEventListener('pagehide', handleGameCloseExitLeaveQuit);

    return (): void => {
      window.removeEventListener('pagehide', handleGameCloseExitLeaveQuit);
    };
  }, [casinoConfig, tableConfig]);

  /** Autoplay */
  useAutoplayBetDispatcher();

  /* Fetchers */
  const casinoConfigFetcher = useCasinoConfigFetcher(true);
  useEffect(() => {
    casinoConfigFetcher();
  }, [casinoConfigFetcher]);

  const envFetcher = useEnvFetcher(true);
  useEffect(() => {
    envFetcher();
  }, [envFetcher]);

  const playerConfigFetcher = usePlayerConfigFetcher(true);
  useEffect(() => {
    playerConfigFetcher();
  }, [playerConfigFetcher]);

  useEffect(() => {
    PlayerConfigFetcher.setIsCrashGame(isCrashGame ?? false);
  }, [isCrashGame]);

  const statsConfigFetcher = useStatsConfigFetcher();
  useEffect(() => {
    statsConfigFetcher();
  }, [statsConfigFetcher]);

  const statsSettingsFetcher = useStatsSettingsFetcher();
  useEffect(() => {
    statsSettingsFetcher();
  }, [statsSettingsFetcher]);

  const dealerNameReady = useGameStateSlice.dealer()?.name;
  const tableNameReady = useConfigSlice.tableName();

  useEffect(() => {
    if (tableNameReady && dealerNameReady) {
      const welcomeMessage = generateWelcomeMessage(Boolean(isCrashGame), tableNameReady, dealerNameReady);
      dispatch(chatActions.storeGameChatMessage(welcomeMessage));
      dispatch(chatActions.storeLastChatMessage(welcomeMessage));
      dispatch(chatActions.storeLiveSupportMessage(LIVE_SUPPORT_WELCOME_MESSAGE));
    }
  }, [dealerNameReady, dispatch, isCrashGame, tableNameReady]);

  const tableConfigFetcher = useTableConfigFetcher(tableConfigOptions, true);
  useEffect(() => {
    tableConfigFetcher();
  }, [tableConfigFetcher]);

  const userInfoFetcher = useUserInfoFetcher(true);
  useEffect(() => {
    userInfoFetcher();
  }, [userInfoFetcher]);

  const versionsFetcher = useVersionsFetcher(true);
  useEffect(() => {
    if (tableConfig) {
      versionsFetcher();
    }
  }, [versionsFetcher, tableConfig]);

  /* Set populate store with game config */
  useEffect(() => {
    if (tableConfig) {
      dispatch(betActions.setBetLimits(tableConfig.betLimits));
      dispatch(betActions.setTableLimits(tableConfig.tableBetLimit));

      if (tableConfig.openTime > 0) {
        dispatch(gameStateActions.setTable({ openTime: tableConfig.openTime }));
      }

      if (tableConfig.tableClosed) {
        Curtain.resurrect();
        dispatch(popupActions.overwritePopup(PopupType.TableClosed));
        dispatch(gameStateActions.setGameAbandoned(true));
      }
    }
  }, [dispatch, tableConfig]);

  useEffect(() => {
    dispatch(settingsActions.setGameVolumeChannels(Object.values(volumeChannels)));
    dispatch(settingsActions.setUiVolumeChannel(uiVolumeChannel));
    dispatch(configActions.setPlayAreaSize(playAreaSize));
    dispatch(betActions.initBetStack(betConfig));
  }, [dispatch, betConfig, playAreaSize, uiVolumeChannel, volumeChannels]);

  const videoStream = useConfigSlice.videoStream();
  useEffect(() => {
    if (videoStream === false) {
      dispatch(popupActions.pushPopup(PopupType.VideoIsDisabled));
    }
  }, [dispatch, videoStream]);

  const areBlockingResourcesReady = useInitializationSlice.getAreBlockingResourcesReady();
  const areVolumeChannelsReady = useSettingsSlice.getVolumeChannelsReady(Object.values(volumeChannels));
  const betsReady = useBetSlice.betsReady();
  const envReady = useConfigSlice.env();
  const isSubscribed = useGameStateSlice.getGameSubscribed();

  const progress = useMemo(() => {
    const loading = [
      true,
      !!envReady,
      !!playerConfig,
      !!tableConfig,
      areFeatureConfigsReady,
      areBlockingResourcesReady,
      areUITranslationsReady,
      areVolumeChannelsReady,
      betsReady,
      chipsReady,
      isSubscribed,
      userInfoReady,
    ];
    return Math.round(
      loading.reduce((value: number, currentValue: boolean) => {
        if (currentValue) {
          value += 100 / loading.length;
        }
        return value;
      }, 0)
    );
  }, [
    areBlockingResourcesReady,
    areFeatureConfigsReady,
    areUITranslationsReady,
    areVolumeChannelsReady,
    betsReady,
    chipsReady,
    envReady,
    isSubscribed,
    playerConfig,
    tableConfig,
    userInfoReady,
  ]);
  useEffect(() => {
    switch (progress) {
      case 100:
        if (!tableConfig?.tableClosed) {
          dispatch(initializationActions.setIsReadyToRender(true));
          CrossOriginCommunication.dispatch(OutgoingMessage.GameLoadEnd, {
            tableId: tableConfig?.operatorGameId,
          });
          CrossOriginCommunication.dispatch(OutgoingMessage.UpdateSettings, { masterVolume: 0 });
          CrossOriginCommunication.dispatch(OutgoingMessage.UpdateWin, { winResultAmount: 0, balance: { currency } });
          CrossOriginCommunication.dispatch(OutgoingMessage.UpdateBets, { totalBet: 0, balance: { currency } });
        }
        break;
      case 0:
        break;
      default:
        CrossOriginCommunication.dispatch(OutgoingMessage.GameLoadProgress, { progress });
        break;
    }
  }, [dispatch, progress, tableConfig]);

  useEffect(() => {
    const callback = (): void => {
      dispatch(configActions.setGameVisible(!document.hidden));
    };
    window.addEventListener('visibilitychange', callback);

    return (): void => {
      window.removeEventListener('visibilitychange', callback);
    };
  }, [dispatch]);

  const isLowBalance = useBetSlice.isLowBalance();
  const curtainVisible = useCurtain(progress);
  useEffect(() => {
    if (userInfoReady && tableConfig && !curtainVisible) {
      /**
       * Display low balance popup on load if balance is not available
       * or is less than min bet size and bets are open and total placed bet is 0.
       */
      if (isLowBalance && isBetsOpenStatus && !hasTotalBet) {
        dispatch(popupActions.pushPopup(PopupType.InsufficientFunds));
      }
    }
  }, [curtainVisible, dispatch, hasTotalBet, isBetsOpenStatus, isLowBalance, tableConfig, userInfoReady]);

  useEffect(() => {
    if (errorCode === ForbiddenErrors.TABLE_BLOCKED_FOR_COUNTRY && currentLanguage) {
      window.location.href = `${window.location.origin}${RESTRICTED_PAGE_URL}?lang=${currentLanguage}`;
    }
  }, [errorCode, currentLanguage]);

  useDragAndDropInit();
  useFullscreen();
  return null;
};
